import { useCallback, useEffect, useState } from "react";
import { Eye, Globe2, Loader2, RefreshCw, Search, UserSearch } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AdminPageHeader } from "./components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;
const REQUEST_STATUSES = ["Nuovo", "In lavorazione", "Gestito"];

const VARIANTS = {
  contatti: {
    title: "Richieste CasaDiRiposoPerTe",
    description: "Contatti, form guidato e newsletter da casadiriposoperte.it e casadiriposoperte.app.",
    path: "richieste-sito-cdrpt",
    types: { contatto: "Contatto", lead: "Form guidato", newsletter: "Newsletter" },
    icon: Globe2,
  },
  candidature: {
    title: "Candidature CasaDiRiposoPerTe",
    description: "Candidature operatori inviate da casadiriposoperte.it e casadiriposoperte.app.",
    path: "candidature-sito-cdrpt",
    types: { candidatura: "Candidatura" },
    icon: UserSearch,
  },
};

function statusClass(status) {
  if (status === "Gestito") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (status === "In lavorazione") return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-blue-100 text-blue-800 border-blue-200";
}

function typeClass(type) {
  if (type === "candidatura") return "bg-rose-100 text-rose-800 border-rose-200";
  if (type === "lead") return "bg-violet-100 text-violet-800 border-violet-200";
  if (type === "newsletter") return "bg-slate-100 text-slate-800 border-slate-200";
  return "bg-cyan-100 text-cyan-800 border-cyan-200";
}

function displayDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("it-IT");
}

function errorMessage(payload, fallback) {
  return typeof payload?.detail === "string" ? payload.detail : fallback;
}

function extraLines(extra) {
  if (!extra || typeof extra !== "object") return "";
  return Object.entries(extra)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}: ${value}`)
    .join("\n");
}

export default function CdrptWebsiteRequestsAdminModule({ variant = "contatti" }) {
  const config = VARIANTS[variant] || VARIANTS.contatti;
  const Icon = config.icon;
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const baseUrl = `${API}/api/admin/commerciale/${config.path}`;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [updatingId, setUpdatingId] = useState("");
  const showTypeFilter = Object.keys(config.types).length > 1;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      if (type) params.set("form_type", type);
      if (status) params.set("stato", status);
      const response = await fetch(`${baseUrl}?${params.toString()}`, { headers });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(errorMessage(payload, "Impossibile caricare le richieste"));
      setItems(payload.data || []);
    } catch (error) {
      toast.error(error.message || "Impossibile caricare le richieste");
    } finally {
      setLoading(false);
    }
  }, [query, status, type, baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (request, nextStatus) => {
    setUpdatingId(request.id);
    try {
      const response = await fetch(`${baseUrl}/${request.id}/stato`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ stato: nextStatus }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(errorMessage(payload, "Aggiornamento stato non riuscito"));
      setItems((current) => current.map((item) => (
        item.id === request.id ? { ...item, stato: nextStatus } : item
      )));
      setSelected((current) => (
        current?.id === request.id ? { ...current, stato: nextStatus } : current
      ));
      window.dispatchEvent(new Event("affidya-commerciale-menu-counts"));
    } catch (error) {
      toast.error(error.message || "Aggiornamento stato non riuscito");
    } finally {
      setUpdatingId("");
    }
  };
  const linkPartnership = async (request) => {
    const strutturaId = window.prompt("ID della struttura condivisa da collegare alla partnership");
    if (!strutturaId) return;
    try {
      const response = await fetch(`${API}/api/admin/cdrpt/partnership/collega-richiesta/${request.id}?struttura_id=${encodeURIComponent(strutturaId)}`, { method: "POST", headers });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(errorMessage(payload, "Collegamento non riuscito"));
      toast.success("Richiesta collegata all'opportunità partnership");
      setSelected({ ...request, partnership_id: payload.partnership_id });
    } catch (error) { toast.error(error.message || "Collegamento non riuscito"); }
  };

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={Icon}
        title={config.title}
        description={config.description}
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
        }
      />

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[220px] flex-1">
          <Label className="text-xs">Cerca</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              className="pl-8"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Nome, email, telefono, messaggio…"
            />
          </div>
        </div>
        {showTypeFilter ? (
          <div className="w-44">
            <Label className="text-xs">Tipologia</Label>
            <Select value={type || "__all__"} onValueChange={(value) => setType(value === "__all__" ? "" : value)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte</SelectItem>
                {Object.entries(config.types).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <div className="w-44">
          <Label className="text-xs">Stato</Label>
          <Select value={status || "__all__"} onValueChange={(value) => setStatus(value === "__all__" ? "" : value)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti gli stati</SelectItem>
              {REQUEST_STATUSES.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="admin-table-sticky-actions w-full text-sm">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="p-3">Ricevuta</th>
                <th className="p-3">Tipologia</th>
                <th className="p-3">Contatto</th>
                <th className="p-3">{variant === "candidature" ? "CV / ruolo" : "Dettaglio"}</th>
                <th className="p-3">Stato</th>
                <th className="p-3">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan="6" className="p-8 text-center text-slate-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Caricamento…</td></tr>
              ) : !items.length ? (
                <tr><td colSpan="6" className="p-8 text-center text-slate-500">Nessuna richiesta trovata.</td></tr>
              ) : items.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50">
                  <td className="whitespace-nowrap p-3 text-xs text-slate-500">
                    <p>{displayDate(item.created_at)}</p>
                    {item.source ? <p className="mt-0.5 text-[11px] text-slate-400">{item.source}</p> : null}
                  </td>
                  <td className="p-3"><Badge className={typeClass(item.form_type)}>{config.types[item.form_type] || item.form_type}</Badge></td>
                  <td className="p-3"><p className="font-medium">{item.name}</p><p className="text-xs text-slate-500">{item.email}{item.phone ? ` · ${item.phone}` : ""}</p></td>
                  <td className="p-3">
                    <p>{item.product_interest || item.organization || item.cv_filename || "—"}</p>
                    <p className="text-xs text-slate-500 line-clamp-2">{item.message || extraLines(item.extra) || "—"}</p>
                  </td>
                  <td className="p-3">
                    <Select value={item.stato || "Nuovo"} onValueChange={(value) => updateStatus(item, value)} disabled={updatingId === item.id}>
                      <SelectTrigger className="h-8 w-36"><Badge className={statusClass(item.stato || "Nuovo")}>{item.stato || "Nuovo"}</Badge></SelectTrigger>
                      <SelectContent>{REQUEST_STATUSES.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-3"><Button size="icon" variant="ghost" title="Vedi tutti i dati" onClick={() => setSelected(item)}><Eye className="h-4 w-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{config.types[selected?.form_type] || "Richiesta"} · {selected?.name}</DialogTitle></DialogHeader>
          {selected ? (
            <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <Detail label="Ricevuta" value={displayDate(selected.created_at)} />
              <Detail label="Stato" value={selected.stato || "Nuovo"} />
              <Detail label="Nome" value={selected.name} />
              <Detail label="Email" value={selected.email} />
              <Detail label="Telefono" value={selected.phone} />
              <Detail label="File CV" value={selected.cv_filename} />
              <Detail label="Zona / prodotto" value={selected.product_interest} />
              <Detail label="Sito" value={selected.source} />
              <Detail label="IP sorgente" value={selected.ip} />
              <div className="sm:col-span-2"><Detail label="Messaggio" value={selected.message} /></div>
              <div className="sm:col-span-2"><Detail label="Dettagli form" value={extraLines(selected.extra)} /></div>
            </div>
          ) : null}
          <DialogFooter>
            {selected && variant !== "candidature" && ["contatto", "lead"].includes(selected.form_type) && selected.organization ? <Button onClick={() => linkPartnership(selected)}>Collega a partnership</Button> : null}
            <Button variant="outline" onClick={() => setSelected(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 whitespace-pre-wrap text-slate-800">{value || "—"}</p>
    </div>
  );
}
